import { useMemo } from 'react';
import { erc20Abi, parseAbi } from 'viem';
import { useReadContracts } from 'wagmi';
import { StakingPool } from 'api/StakingAPI';
import { useTokenPrices } from 'utils/hooks/useTokenPrices';
import {
  applyStakingPoolValues,
  getStakingPoolValueKey,
  resolveStakingPoolValue,
  StakingPoolLiveInput,
  StakingPoolValue,
  sumResolvedStakingPoolValues,
} from '../utils/stakingLiveValue';
import { getStakingPoolTokenPrice } from '../utils/stakingTokenPrice';

export const STAKING_VALUE_REFRESH_INTERVAL = 15_000;

const earlyUnlockFeesAbi = parseAbi([
  'function earlyUnlockFees() view returns (uint256)',
]);

export type LiveStakingPoolValues = {
  pools: StakingPool[];
  totalTvlUsd: number;
  values: Record<string, StakingPoolValue>;
};

export const useStakingPoolValues = (
  sourcePools: readonly StakingPool[] | undefined
) => {
  const pools = useMemo(() => sourcePools ?? [], [sourcePools]);
  const tokenPricesQuery = useTokenPrices({
    query: {
      refetchInterval: STAKING_VALUE_REFRESH_INTERVAL,
      staleTime: STAKING_VALUE_REFRESH_INTERVAL,
    },
  });
  const contracts = useMemo(
    () =>
      pools.flatMap((pool) => [
        {
          abi: erc20Abi,
          address: pool.depositTokenAddress,
          functionName: 'balanceOf' as const,
          chainId: Number(pool.chainId),
          args: [pool.stakingContractAddress] as const,
        },
        {
          abi: earlyUnlockFeesAbi,
          address: pool.stakingContractAddress,
          functionName: 'earlyUnlockFees' as const,
          chainId: Number(pool.chainId),
        },
      ]),
    [pools]
  );
  const balancesQuery = useReadContracts({
    contracts,
    query: {
      enabled: contracts.length > 0,
      refetchInterval: STAKING_VALUE_REFRESH_INTERVAL,
      staleTime: STAKING_VALUE_REFRESH_INTERVAL,
    },
  });

  const data = useMemo<LiveStakingPoolValues>(() => {
    const values: Record<string, StakingPoolValue> = {};

    pools.forEach((pool, index) => {
      const balanceResult = balancesQuery.data?.[index * 2];
      const feeResult = balancesQuery.data?.[index * 2 + 1];
      let liveInput: StakingPoolLiveInput | undefined;

      if (
        balanceResult?.status === 'success' &&
        feeResult?.status === 'success'
      ) {
        liveInput = {
          balance: balanceResult.result as bigint,
          earlyUnlockFees: feeResult.result as bigint,
          tokenDecimals: pool.depositTokenDecimals,
          tokenPriceUsd: getStakingPoolTokenPrice(
            tokenPricesQuery.data,
            pool
          ),
        };
      }

      values[getStakingPoolValueKey(pool)] = resolveStakingPoolValue(
        pool,
        liveInput
      );
    });

    return {
      pools: applyStakingPoolValues(pools, values),
      totalTvlUsd: sumResolvedStakingPoolValues(pools, values),
      values,
    };
  }, [balancesQuery.data, pools, tokenPricesQuery.data]);

  return {
    ...balancesQuery,
    data,
    error: balancesQuery.error ?? tokenPricesQuery.error,
    isLoading: balancesQuery.isLoading || tokenPricesQuery.isLoading,
    refetch: async () => {
      await Promise.all([
        balancesQuery.refetch(),
        tokenPricesQuery.refetch(),
      ]);
    },
  };
};
